/**
 * A chip: a short word on a tinted pill.
 *
 * "Archived", "Pending", "Overdue", "Recurring", "Never enrolled" - the CRM's whole status
 * vocabulary. In XAML each of these was a `Border` with a hard-coded corner radius, padding and a
 * pair of brushes repeated at every site, which is how three of the knowledge base's four chips came
 * to fail WCAG AA in light mode without anyone noticing. Here there is one control and one table,
 * so a contrast fix is made once.
 *
 * `autoWidth` on both the chip and its label is what makes it hug its text; without it a chip in a
 * horizontal stack is 120px of empty pill with a word at the left.
 */
import { insets, type PanelOptions } from 'webwin';
import { Card, type CardTint } from './Card.js';
import { CrmLabel, type TextRole } from './Text.js';
import { FONT } from './layout.js';

export type ChipTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand';

const TINT: Record<ChipTone, CardTint> = {
  neutral: 'alt',
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
  info: 'info',
  brand: 'brand'
};

const INK: Record<ChipTone, TextRole> = {
  neutral: 'muted',
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
  info: 'info',
  brand: 'onBrand'
};

export interface ChipOptions extends PanelOptions {
  tone?: ChipTone;
}

export class Chip extends Card {
  readonly label: CrmLabel;
  #tone: ChipTone;

  constructor(text: string, options: ChipOptions = {}) {
    const tone = options.tone ?? 'neutral';
    super({
      tint: TINT[tone],
      radius: 3,
      bordered: false,
      padding: insets(7, 2),
      autoWidth: true,
      autoHeight: true,
      hAlign: 'start',
      vAlign: 'center',
      ...options
    });
    this.label = new CrmLabel({
      text,
      font: FONT.tiny,
      role: INK[tone],
      // A chip wider than its column wraps inside itself rather than running past the card.
      wrap: true,
      autoWidth: true,
      autoHeight: true
    });
    this.controls.add(this.label);
    this.#tone = tone;
  }

  /**
   * The tone, settable.
   *
   * A chip whose wording is fixed can be built once, but several of these say something that depends
   * on what the machine reported - battery health is green, amber or red, and the TPM is green or
   * amber - and rebuilding the control to change its colour would throw away the row it is in. The
   * pair must move together, which is the whole reason this is one setter and not two fields.
   */
  get tone(): ChipTone {
    return this.#tone;
  }
  set tone(value: ChipTone) {
    if (this.#tone === value) return;
    this.#tone = value;
    this.tint = TINT[value];
    this.label.role = INK[value];
    this.invalidate();
  }

  get text(): string {
    return this.label.text;
  }
  set text(value: string) {
    this.label.text = value;
  }
}

/** The one-liner every card row uses. */
export function chip(text: string, tone: ChipTone = 'neutral', options: ChipOptions = {}): Chip {
  return new Chip(text, { tone, ...options });
}
